import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams } from 'ag-grid-enterprise';
import { AppState } from '$/store';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { ClientAnalyticsService } from '$/services';
import AnalyticsHeaderFilter from '$/components/analyticsHeaderFilter';
import NotFound from '$/pages/not-found';
import { CLIENT_ANALYTICS_VIEW } from '$/core/constants';
import BixLogoArch from '$/assets/BixLogoArch.svg';
import FilterButtons, { IFilterButton } from '$/components/filterButtons';
import FilterCheckboxes, { IFilterCheckbox } from '$/components/filterCheckboxes';
import { getPublicCompaniesFeatureFlagState } from '$/store/release-feature-flags/selectors';
import './index.scss';

interface IAnalyticsProps {
  isPublicCompaniesEnabled: boolean;
  featureFlags?: string[];
}

const PUBLIC_COMPANIES_LABEL = 'Public Companies';
const ALL_COMPANIES_LABEL = 'All Companies';
const PRIVATE_COMPANIES_LABEL = 'Private Companies';
const US_COMPANIES_LABEL = 'US Companies';
const EUROPE_COMPANIES_LABEL = 'Europe Companies';

const Analytics: React.FC<IAnalyticsProps> = props => {
  const { isPublicCompaniesEnabled, featureFlags: featureFlags = [] } = props;

  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isLoading, setIsLoading] = useState(null as boolean | null);
  const [analytics, setAnalytics] = useState(undefined as Bixby.Models.CompanyAnalyticsResult | undefined);
  const [dataForGrid, setDataForGrid] = useState([] as Bixby.Models.CompanyAnalytics[]);
  const [dataError, setDataError] = useState(undefined as string | undefined);
  const [dataRetrievalComplete, setDataRetrievalComplete] = useState(false as boolean);
  const [companyFilters, setCompanyFilters] = useState([
    { id: ALL_COMPANIES_LABEL, name: ALL_COMPANIES_LABEL, selected: true },
    { id: PRIVATE_COMPANIES_LABEL, name: PRIVATE_COMPANIES_LABEL, selected: false },
    { id: PUBLIC_COMPANIES_LABEL, name: PUBLIC_COMPANIES_LABEL, selected: false },
  ] as IFilterButton[]);
  const [companyRegionFilters, setCompanyRegionFilters] = useState([
    { id: US_COMPANIES_LABEL, name: US_COMPANIES_LABEL, selected: true },
    { id: EUROPE_COMPANIES_LABEL, name: EUROPE_COMPANIES_LABEL, selected: true },
  ] as IFilterCheckbox[]);
  const [hasSelectedCompanyFilters, setHasSelectedCompanyFilters] = useState(true as boolean);
  const [hasSelectedRegionFilters, setHasSelectedRegionFilters] = useState(true as boolean);

  const analyticsMetrics: Bixby.Enums.AnalyticsMetrics[] = Object.values(Bixby.Enums.AnalyticsMetrics);

  useEffect(() => {
    async function getAnalytics() {
      setIsLoading(true);
      try {
        const result: Bixby.Models.CompanyAnalyticsResult = await ClientAnalyticsService.getCompanyAnalytics();
        setAnalytics(result);
        if (isPublicCompaniesEnabled) {
          setDataForGrid(result.allCompanies || []);
        } else {
          setDataForGrid(result.privateCompanies || []);
        }
      } catch (e: any) {
        setDataError('Error retrieving analytics');
      } finally {
        setIsLoading(false);
        setDataRetrievalComplete(true);
      }
    }

    if (featureFlags.includes(CLIENT_ANALYTICS_VIEW)) {
      getAnalytics();
    }
  }, [featureFlags, isPublicCompaniesEnabled]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const valueFormatter = (params: any, formatter: (value: any) => string, fallback: string) => {
    if (params.value !== null && params.value !== undefined) {
      return formatter(params.value);
    } else if (!params.node.allChildrenCount) {
      return fallback;
    }
    return '';
  };

  class AnalyticsIdentifierCellRenderer {
    eGui: any;

    init(params: ICellRendererParams) {
      const newDiv = document.createElement('div');
      let value: string = params.value;

      if (value.includes('Bixby Loan Market Data')) {
        const [label, filterType] = value.split(' - ');
        if (isPublicCompaniesEnabled) {
          newDiv.innerHTML = `<span><img class="bixby-logo" src=${BixLogoArch} alt='Bixby Logo'>${label}<span class="bixby-filter-type"> - ${filterType.toUpperCase()}</span></span>`;
        } else {
          newDiv.innerHTML = `<span><img class="bixby-logo" src=${BixLogoArch} alt='Bixby Logo'>Bixby Private Loan Index</span>`;
        }
      } else {
        const isArray = Bixby.Utils.isArray(params.value);
        if (isArray) {
          value = params.value[params.value.length - 1];
        }
        newDiv.innerHTML = `<span>${value}</span>`;
      }
      this.eGui = newDiv.firstChild;
    }

    getGui() {
      return this.eGui;
    }
  }

  const formatCurrency = (value: number, fallback: string) => {
    const parsedValue: number = parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return fallback;
    }

    // add in appropriate formatting
    let prefix = '$';
    let suffix = '';
    if (parsedValue < 0) {
      prefix = prefix + '(';
      suffix = suffix + ')';
    }

    // remove hyphen
    const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

    const currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,').split('.')[0];

    return prefix + currency + suffix;
  };

  const formatMultiplier = (value: number) => {
    const percentage = value.toFixed(1);
    return `${percentage}x`;
  };

  const formatPercentage = (value: number, fractionDigits?: number | undefined) => {
    const percentage = (value * 100).toFixed(fractionDigits || 0);
    return `${percentage}%`;
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'expandAll', 'contractAll', 'separator', 'export'];
    return result;
  };

  const getRowHeight = (params: any) => {
    if (analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)) {
      return 20;
    }
    return 32;
  };

  const handleFilter = (id: string, futureState: boolean) => {
    const newFilters = companyFilters.map(_ => ({ ..._, selected: _.id === id ? futureState : false }));
    if (!newFilters.every(_ => !_.selected)) {
      setCompanyFilters(newFilters);
      setHasSelectedCompanyFilters(true);
      setHasSelectedRegionFilters(true);

      const includeUs = companyRegionFilters.find(_ => _.id === US_COMPANIES_LABEL)?.selected || true;
      const includeEurope = companyRegionFilters.find(_ => _.id === EUROPE_COMPANIES_LABEL)?.selected || true;

      setFilters(id, includeUs, includeEurope);
    } else setHasSelectedCompanyFilters(false);
  };

  const handleRegionFilter = (id: string, futureState: boolean) => {
    const newRegionFilters = companyRegionFilters.map(_ => ({
      ..._,
      selected: _.id === id ? futureState : _.selected,
    }));
    if (!newRegionFilters.every(_ => !_.selected)) {
      setCompanyRegionFilters(newRegionFilters);
      setHasSelectedRegionFilters(true);
      setHasSelectedCompanyFilters(true);
      const companyFilter = companyFilters.find(_ => _.selected);
      const selectedCompanyFilter = companyFilter?.id || PUBLIC_COMPANIES_LABEL;

      let includeUs: boolean;
      let includeEurope: boolean;

      if (id === US_COMPANIES_LABEL) {
        includeUs = futureState;
        includeEurope = companyRegionFilters.find(_ => _.id === EUROPE_COMPANIES_LABEL)?.selected || true;
      } else {
        includeUs = companyRegionFilters.find(_ => _.id === US_COMPANIES_LABEL)?.selected || true;
        includeEurope = futureState;
      }

      setFilters(selectedCompanyFilter, includeUs, includeEurope);
    } else setHasSelectedRegionFilters(false);
  };

  const setFilters = (currentCompanyFilterId: string, includeUs: boolean, includeEurope: boolean) => {
    if (!analytics) return;

    if (currentCompanyFilterId === PUBLIC_COMPANIES_LABEL && includeUs && includeEurope) {
      setDataForGrid(analytics.publicCompanies || []);
    } else if (currentCompanyFilterId === PUBLIC_COMPANIES_LABEL && includeUs && !includeEurope) {
      setDataForGrid(analytics.publicCompaniesUs || []);
    } else if (currentCompanyFilterId === PUBLIC_COMPANIES_LABEL && !includeUs && includeEurope) {
      setDataForGrid(analytics.publicCompaniesEurope || []);
    } else if (currentCompanyFilterId === PRIVATE_COMPANIES_LABEL && includeUs && includeEurope) {
      setDataForGrid(analytics.privateCompanies || []);
    } else if (currentCompanyFilterId === PRIVATE_COMPANIES_LABEL && includeUs && !includeEurope) {
      setDataForGrid(analytics.privateCompaniesUs || []);
    } else if (currentCompanyFilterId === PRIVATE_COMPANIES_LABEL && !includeUs && includeEurope) {
      setDataForGrid(analytics.privateCompaniesEurope || []);
    } else if (currentCompanyFilterId === ALL_COMPANIES_LABEL && includeUs && includeEurope) {
      setDataForGrid(analytics.allCompanies || []);
    } else if (currentCompanyFilterId === ALL_COMPANIES_LABEL && includeUs && !includeEurope) {
      setDataForGrid(analytics.allCompaniesUs || []);
    } else if (currentCompanyFilterId === ALL_COMPANIES_LABEL && !includeUs && includeEurope) {
      setDataForGrid(analytics.allCompaniesEurope || []);
    } else setDataForGrid([]);
  };

  if (!featureFlags.includes(CLIENT_ANALYTICS_VIEW)) {
    return <NotFound />;
  }

  if (!Bixby.Utils.isNullOrEmpty(dataError)) {
    return (
      <React.Fragment>
        <section className="analytics-section">
          <HeadingBlock title="Analytics" />
          <div className="analytics-error-section">
            <p className="error">{dataError}</p>
          </div>
        </section>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <section className="analytics-section">
          <HeadingBlock title="Analytics" />
          {isLoading && <Loading pageLevel />}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'left' }}>
              {isPublicCompaniesEnabled && <FilterButtons filters={companyFilters} onFilter={handleFilter} />}
            </div>
            <div style={{ textAlign: 'right' }}>
              <FilterCheckboxes filters={companyRegionFilters} onFilter={handleRegionFilter} />
            </div>
          </div>
          {!hasSelectedCompanyFilters ? (
            <div className="error">At least one company type filter must be selected</div>
          ) : null}
          {!hasSelectedRegionFilters ? <div className="error">At least one region filter must be selected</div> : null}
          {dataForGrid && !Bixby.Utils.isNullOrEmpty(dataForGrid) ? (
            <div className="analytics-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    defaultColDef={{
                      flex: 1,
                      minWidth: 150,
                      resizable: true,
                      lockPinned: true,
                      filter: 'agNumberColumnFilter',
                      cellClass: 'align-center',
                    }}
                    onGridReady={onGridReady}
                    rowData={dataForGrid}
                    suppressDragLeaveHidesColumns={true}
                    suppressClipboardPaste={true}
                    suppressMovableColumns={true}
                    getContextMenuItems={getContextMenuItems}
                    treeData={true}
                    animateRows={true}
                    groupDefaultExpanded={-1}
                    groupDisplayType="custom"
                    getDataPath={function (data) {
                      return data.name;
                    }}
                    rowClassRules={{
                      metric: params => {
                        return analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics);
                      },
                    }}
                    getRowHeight={getRowHeight}
                    headerHeight={32}
                    components={{
                      headerFilter: AnalyticsHeaderFilter,
                      AnalyticsIdentifierCellRenderer: AnalyticsIdentifierCellRenderer,
                    }}
                    columnDefs={[
                      {
                        headerGroupComponent: 'headerFilter',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'name',
                            cellRenderer: 'agGroupCellRenderer',
                            valueFormatter: params => params.node?.key || '',
                            cellClassRules: {
                              sector: params => {
                                return (
                                  params.node.level === 0 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                              'industry-group': params => {
                                return (
                                  params.node.level === 1 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                              industry: params => {
                                return (
                                  params.node.level === 2 &&
                                  !analyticsMetrics.includes(params.node.key as Bixby.Enums.AnalyticsMetrics)
                                );
                              },
                            },
                            headerComponentParams: {
                              template:
                                '<div class="analytics-name"><span class="sector">Sector</span> | <span class="industry-group">Industry Group</span> | <span class="industry">Industry</span></div>',
                            },
                            minWidth: 350,
                            cellRendererParams: { suppressCount: true, innerRenderer: AnalyticsIdentifierCellRenderer },
                            headerClass: 'locked-header-note',
                            pinned: 'left',
                            showRowGroup: true,
                          },
                        ],
                      },
                      {
                        headerName: 'EBITDA Leverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLienDebtGrossEbitda',
                            headerName: '1st Lien',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                          {
                            field: 'totalDebtGrossEbitda',
                            headerName: 'Total',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'Adj. EBITDA Leverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLienDebtGrossAdjustedEbitda',
                            headerName: '1st Lien',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                          {
                            field: 'totalDebtGrossAdjustedEbitda',
                            headerName: 'Total',
                            minWidth: 100,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'Interest Coverage',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'interestCoverageEbitda',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                          {
                            field: 'interestCoverageAdjustedEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'Interest Coverage (After Capex)',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'interestCoverageAfterCapitalExpendituresEbitda',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                          {
                            field: 'interestCoverageAfterCapitalExpendituresAdjustedEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                        ],
                      },
                      {
                        field: 'liquidity',
                        headerName: 'Liquidity (1)',
                        minWidth: 130,
                        valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                      },
                      {
                        headerName: 'MRQ Growth %',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'revenueGrowth',
                            headerName: 'Revenue',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                          {
                            field: 'ebitdaGrowth',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                          {
                            field: 'adjustedEbitdaGrowth',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'Margin %',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'ebitdaMargin',
                            headerName: 'EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                          {
                            field: 'adjustedEbitdaMargin',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                          {
                            field: 'ocfMargin',
                            headerName: 'OCF',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                          {
                            field: 'capitalExpendituresMargin',
                            headerName: 'OCF-Capex',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatPercentage, 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: '1st Lien Loan Details',
                        cellClass: 'grid-cell-centered',
                        children: [
                          {
                            field: 'firstLien1Amount',
                            headerName: '$ Amt.',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                            cellClass: 'align-left',
                          },
                          {
                            field: 'firstLien1Spread',
                            headerName: 'Spread',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(
                                params,
                                (value: any) => {
                                  return value;
                                },
                                'NM'
                              ),
                          },
                        ],
                      },
                      {
                        field: 'totalEnterpriseValue',
                        headerName: 'TEV',
                        minWidth: 100,
                        valueFormatter: params =>
                          valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                      },
                      {
                        headerName: 'Current Loan-to-Value (2)',
                        children: [
                          {
                            field: 'firstLienDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: '1st Lien',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          },
                          {
                            field: 'totalDebtNetAdjustedEbitdaByTotalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: 'Total',
                            minWidth: 120,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'TEV /',
                        children: [
                          {
                            field: 'totalEnterpriseValueByRevenue',
                            headerName: 'Revenue',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                          {
                            field: 'totalEnterpriseValueByAdjustedManagementEbitda',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params => valueFormatter(params, formatMultiplier, 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'Financials at transaction',
                        children: [
                          {
                            field: 'revenueAtBuyout',
                            headerName: 'Revenue',
                            minWidth: 130,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                          },
                          {
                            field: 'managementAdjustedEbitdaAtBuyout',
                            headerName: 'Adj. EBITDA',
                            minWidth: 130,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatCurrency(value, 'NM'), 'NM'),
                          },
                        ],
                      },
                      {
                        headerName: 'Capital Structure at transaction',
                        children: [
                          {
                            field: 'netDebtPercentageOfCap',
                            headerName: 'Net Debt % of Cap',
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            minWidth: 200,
                          },
                          {
                            field: 'totalEquityPercentageOfCap',
                            headerName: 'Total Equity % of Cap',
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            minWidth: 200,
                          },
                        ],
                      },
                      {
                        headerName: 'Change since transaction',
                        children: [
                          {
                            field: 'cumulativeDividendsByImpliedEquityAtRisk',
                            headerName: `Dividends / Equity "at risk" (3)`,
                            valueFormatter: params =>
                              valueFormatter(params, (value: any) => formatPercentage(value, 1), 'NM'),
                            minWidth: 250,
                          },
                        ],
                      },
                    ]}></AgGridReact>
                </div>
              </div>
              <p className="analytics-note">(1) Calculated as Cash + Available Line of Credit divided by Total Debt.</p>
              <p className="analytics-note">
                (2) Calculated as current 1st Lien (Total) Net Leverage divided by TEV / Adj. EBITDA at the time of
                transaction.
              </p>
              <p className="analytics-note">
                (3) A negative Dividends as % of initial equity contribution ratio reflects subsequent net equity
                contributions.
              </p>
            </div>
          ) : dataRetrievalComplete ? (
            <div className="analytics-not-found-section">
              <NoDataFound dataType="analytics" />
            </div>
          ) : null}
        </section>
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isPublicCompaniesEnabled } = getPublicCompaniesFeatureFlagState(state);
  return {
    featureFlags: state.loginState.featureFlags,
    isPublicCompaniesEnabled,
  };
};

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Analytics);
